// /frontend/src/components/Header.jsx
import React, { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toggleTheme, getSavedTheme, THEMES } from "../utils/themeToggle";
import "../assets/styles/Header.css";
import FancyUnderline from "./FancyUnderline";
import { useAuth } from "../context/AuthContext";

/** ─────────────────────────────
 *  Utility: Detect click outside for dropdown
 *  ───────────────────────────── */
function useClickOutside(ref, onOutside) {
  useEffect(() => {
    const onClick = (e) => {
      if (!ref.current) return;
      if (!ref.current.contains(e.target)) onOutside?.();
    };
    const onEsc = (e) => {
      if (e.key === "Escape") onOutside?.();
    };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onEsc);
    };
  }, [ref, onOutside]);
}

const Header = () => {
  const navigate = useNavigate();
  const { authToken } = useAuth();

  const [theme, setTheme] = useState(() => getSavedTheme());
  const [mobileOpen, setMobileOpen] = useState(false);

  // ✅ theme toggle (คง util เดิม)
  const handleToggle = useCallback(() => {
    const next = toggleTheme();
    setTheme(next);
  }, []);

  // ✅ sync theme จาก storage + expose ให้ CSS ใช้ได้ (data-theme + class)
  useEffect(() => {
    const saved = getSavedTheme();
    setTheme(saved);

    const root = document.documentElement;
    root.setAttribute("data-theme", saved);
    root.classList.toggle("ui-dark", saved === THEMES.DARK);
    root.classList.toggle("ui-light", saved !== THEMES.DARK);
  }, []);

  // ✅ update attr/class ทุกครั้งที่ theme เปลี่ยน
  useEffect(() => {
    const root = document.documentElement;
    root.setAttribute("data-theme", theme);
    root.classList.toggle("ui-dark", theme === THEMES.DARK);
    root.classList.toggle("ui-light", theme !== THEMES.DARK);
  }, [theme]);

  // ✅ NavItem: รองรับ afterClick เพื่อให้ Dropdown ปิดได้
  const NavItem = ({
    to,
    children,
    className = "",
    title = "",
    afterClick,
  }) => (
    <button
      className={`nav-item ${className}`}
      onClick={() => {
        navigate(to);
        setMobileOpen(false);
        afterClick?.();
      }}
      title={title}
      aria-label={title || (typeof children === "string" ? children : "nav item")}
    >
      <FancyUnderline>{children}</FancyUnderline>
    </button>
  );

  /** ─────────────────────────────
   *  Dropdown group (accessible)
   *  ───────────────────────────── */
  const Dropdown = ({ label, children }) => {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    useClickOutside(ref, () => setOpen(false));

    // ✅ clone ลูกในเมนู เพื่อ inject afterClick ปิด dropdown อัตโนมัติ
    const clonedChildren = React.Children.map(children, (child) => {
      if (!React.isValidElement(child)) return child;
      return React.cloneElement(child, {
        afterClick: () => setOpen(false),
      });
    });

    return (
      <div className="nav-dropdown" ref={ref}>
        <button
          className="nav-item dropdown-toggle"
          aria-haspopup="menu"
          aria-expanded={open ? "true" : "false"}
          onClick={() => setOpen((v) => !v)}
        >
          <FancyUnderline>{label} ▾</FancyUnderline>
        </button>

        {open && (
          <div className="dropdown-menu" role="menu">
            {clonedChildren}
          </div>
        )}
      </div>
    );
  };

  /** ─────────────────────────────
   *  Menus (คงรายการเดิมทั้งหมด)
   *  ───────────────────────────── */
  const ProductsMenu = (
    <Dropdown label="Products">
      <NavItem to="/compare" className="dropdown-item" title="เปรียบเทียบเบี้ยประกันรถยนต์">
        เปรียบเทียบประกันรถ
      </NavItem>

      <NavItem to="/car-brands" className="dropdown-item" title="ค้นหาประกันรถตามยี่ห้อ–รุ่น">
        ค้นหาตามรุ่นรถ
      </NavItem>

      <NavItem to="/compare/core" className="dropdown-item" title="ดูแพ็กเกจแนะนำ">
        แพ็กเกจแนะนำ
      </NavItem>

      <NavItem to="/highlights" className="dropdown-item" title="แพ็กเกจไฮไลท์ (1/2+/3+)">
        Highlights
      </NavItem>

      <NavItem to="/packages" className="dropdown-item">
        แพ็กเกจทั้งหมด
      </NavItem>

      <NavItem to="/prb" className="dropdown-item" title="ประกันภัยภาคบังคับ (พ.ร.บ.)">
        พ.ร.บ. รถยนต์
      </NavItem>

      <NavItem to="/taladrod" className="dropdown-item">
        ตลาดรถ
      </NavItem>

      <NavItem to="/prb/cards" className="dropdown-item" title="คู่มือ พ.ร.บ. & เงื่อนไขแบบการ์ด">
        PRB Knowledge Cards
      </NavItem>

      <NavItem to="/vtravel" className="dropdown-item">
        V-Travel
      </NavItem>

      <NavItem to="/cargo-wizard" className="dropdown-item">
        Cargo Wizard
      </NavItem>

      <NavItem to="/gallery" className="dropdown-item">
        แกลเลอรี
      </NavItem>

      <NavItem to="/new-flow" className="dropdown-item" title="ค้นหาแบบขั้นตอนใหม่">
        New Flow Wizard
      </NavItem>
    </Dropdown>
  );

  const ToolsMenu = (
    <Dropdown label="Tools">
      <NavItem to="/georisk" className="dropdown-item" title="ประเมินความเสี่ยงพื้นที่จากพิกัด">
        GeoRisk
      </NavItem>

      <NavItem to="/risk" className="dropdown-item" title="Industrial Risk Console">
        Industrial Risk
      </NavItem>

      <NavItem to="/risk/hub" className="dropdown-item" title="Cheetah Risk Engineering Hub">
        Risk Engineering Hub
      </NavItem>

      <NavItem
        to="/risk/checklist-generator"
        className="dropdown-item"
        title="Risk Survey Checklist Generator"
      >
        Risk Survey Checklist
      </NavItem>

      <NavItem
        to="/risk/survey-checklist-form"
        className="dropdown-item"
        title="แบบฟอร์ม Survey Checklist (โรงงานอุตสาหกรรม)"
      >
        Survey Checklist Form
      </NavItem>

      <NavItem to="/fnol" className="dropdown-item" title="แจ้งอุบัติเหตุออนไลน์">
        แจ้งเหตุ (FNOL)
      </NavItem>

      <NavItem to="/public/preset-estimator" className="dropdown-item">
        Estimator Demo
      </NavItem>

      {/* ✅ AURORA Cost Engine */}
      <NavItem
        to="/aurora"
        className="dropdown-item"
        title="AURORA — Algorithmic Uncertainty & Risk Optimized Resource Assessor"
      >
        AURORA Cost Engine
      </NavItem>

      <NavItem
        to="/embed/aurora"
        className="dropdown-item"
        title="AURORA (Embed mode)"
      >
        AURORA (Embed)
      </NavItem>


      <NavItem
        to="/damage-lab"
        className="dropdown-item"
        title="ห้องทดลองความเสียหายรถยนต์ (Damage Heatmap + Parts)"
      >
        Car Damage Lab
      </NavItem>

      <NavItem
        to="/public/desktop-survey"
        className="dropdown-item"
        title="แบบสำรวจสถานประกอบการ (Public)"
      >
        Desktop Survey (Public)
      </NavItem>

      <NavItem to="/quiz" className="dropdown-item">
        Quiz Game
      </NavItem>

      <NavItem to="/flood-escape" className="dropdown-item" title="เกมหนีเส้นทางน้ำท่วม — Flood Escape">
        Flood Escape Map 🌊
      </NavItem>

      <NavItem to="/flood-fire-lab" className="dropdown-item" title="เกมกริดหนีน้ำท่วม + Hazard ถังดับเพลิง">
        FloodFire Lab 🔥
      </NavItem>

      <NavItem to="/ocr" className="dropdown-item">
        OCR
      </NavItem>

      <NavItem
        to="/photo-assist"
        className="dropdown-item"
        title="อัปโหลดเอกสารเพื่ออ่านข้อมูลกรมธรรม์อัตโนมัติ"
      >
        Photo Assist
      </NavItem>

      <NavItem to="/policy-motor-table" className="dropdown-item">
        Policy Table 1.1
      </NavItem>
      <NavItem to="/policy-motor-table-1-2" className="dropdown-item">
        Policy Table 1.2
      </NavItem>
      <NavItem to="/policy-motor-table-1-3" className="dropdown-item">
        Policy Table 1.3
      </NavItem>

      <NavItem to="/policy-endorsement-table-2-1" className="dropdown-item">
        Endorsement Table 2.1
      </NavItem>

      <NavItem to="/policy-interactive" className="dropdown-item">
        Interactive Policy
      </NavItem>

      <NavItem to="/debug/voice" className="dropdown-item" title="ทดสอบ Voice → Slots → BigQuery Quote">
        Voice Debug
      </NavItem>

      <NavItem to="/serp" className="dropdown-item" title="วิจัย SERP และ Trends">
        SERP Lab
      </NavItem>
    </Dropdown>
  );

  const ForumMenu = (
    <Dropdown label="Forum">
      <NavItem to="/forum" className="dropdown-item" title="พูดคุย ถาม-ตอบ เรื่องประกันภัย">
        ชุมชน/รวมกระทู้
      </NavItem>

      {authToken && (
        <NavItem to="/forum/new" className="dropdown-item" title="ตั้งคำถามหรือแบ่งปันความรู้ในฟอรั่ม">
          สร้างกระทู้
        </NavItem>
      )}
    </Dropdown>
  );

  return (
    <header
      className={`header ${theme === THEMES.DARK ? "header--dark" : "header--light"}`}
      data-theme={theme}
    >
      {/* Logo → Home */}
      <button
        className="logo"
        onClick={() => {
          navigate("/");
          setMobileOpen(false);
        }}
        aria-label="Go to home"
      >
        Cheetah Broker
      </button>

      {/* Mobile hamburger */}
      <button
        className="hamburger"
        aria-label="Toggle navigation"
        aria-expanded={mobileOpen ? "true" : "false"}
        onClick={() => setMobileOpen((v) => !v)}
      >
        ☰
      </button>

      {/* Main Nav */}
      <nav className={`nav-links ${mobileOpen ? "open" : ""}`}>
        {ProductsMenu}
        {ToolsMenu}
        {ForumMenu}

        <NavItem to="/prb" className="nav-item--primary" title="ซื้อ/ต่ออายุ พ.ร.บ. รถยนต์">
          พ.ร.บ. รถยนต์
        </NavItem>

        <NavItem to="/risk" className="nav-item--primary" title="Industrial Risk Console">
          Industrial Risk
        </NavItem>

        <NavItem to="/risk/hub" className="nav-item--primary" title="Cheetah Risk Engineering Hub">
          Risk Engineering Hub
        </NavItem>

        <NavItem to="/risk/checklist-generator" className="nav-item" title="Risk Survey Checklist Generator">
          Risk Survey Checklist
        </NavItem>

        <NavItem to="/new-flow" className="nav-item--primary" title="ค้นหาแพ็กเกจแบบขั้นตอนใหม่">
          เริ่มค้นหาใหม่
        </NavItem>

        {!authToken ? (
          <>
            <NavItem to="/login">Login</NavItem>
            <NavItem to="/register">Register</NavItem>
            <NavItem to="/admin/login">Admin</NavItem>
            <NavItem to="/login?role=agent">Agent</NavItem>
          </>
        ) : (
          <NavItem to="/profile">Profile</NavItem>
        )}

        {/* Theme toggle */}
        <button
          className="btn-toggle"
          onClick={handleToggle}
          title="Toggle Theme"
          aria-label="Toggle Theme"
        >
          {theme === THEMES.DARK ? "🌞" : "🌙"}
        </button>
      </nav>
    </header>
  );
};

export default Header;
